// Brands a Nepali utensils shop actually stocks, offered per category.
//
// Researched rather than invented -- sources are listed in
// docs/product-families.md alongside the item list.
//
// Deliberately thin for steel and brass. Nepali steel utensils are largely
// unbranded: a karai or a thali comes from a local rolling mill with no name on
// it at all. Padding those lists to look complete would put brands in front of
// the shopkeeper that nobody sells, which is worse than an empty dropdown --
// the field stays free text, so a real brand can always be typed.
//
// The shop's own brands rank above these anyway (see suggestBrands), so this
// list only has to be useful on day one.

export const BRANDS = [
  // Pressure cookers and boxed goods. The Indian names dominate; Baltra is
  // Nepali and CG, Himstar and Yasuda are Nepali houses selling both.
  { name: "Prestige", cats: ["boxed", "electric"] },
  { name: "Hawkins", cats: ["boxed"] },
  { name: "Butterfly", cats: ["boxed", "electric"] },
  { name: "Vinod", cats: ["boxed"] },
  { name: "Pigeon", cats: ["boxed", "electric"] },
  { name: "Bajaj", cats: ["boxed", "electric"] },
  { name: "Diamond", cats: ["boxed"] },

  // Electric. Baltra has been Nepali-owned since 1994 and is the one most
  // shops carry; Himstar belongs to Him Electronics of the Golchha group.
  { name: "Baltra", cats: ["electric", "boxed"] },
  { name: "Yasuda", cats: ["electric", "boxed"] },
  { name: "CG", cats: ["electric", "boxed"] },
  { name: "Himstar", cats: ["electric"] },
  { name: "Home Glory", cats: ["electric"] },
  { name: "Electron", cats: ["electric"] },
  { name: "Better", cats: ["electric"] },
  { name: "Tulip", cats: ["electric"] },
  { name: "Philips", cats: ["electric"] },
  { name: "Panasonic", cats: ["electric"] },

  // Plastic. National Plastic and Bagmati are Nepali manufacturers; Cello and
  // Milton come across the border.
  { name: "Cello", cats: ["plastic", "boxed"] },
  { name: "Milton", cats: ["plastic", "boxed"] },
  { name: "National Plastic", cats: ["plastic"] },
  { name: "Bagmati", cats: ["plastic"] },
  { name: "Dolphin", cats: ["plastic"] },
  { name: "Nayasa", cats: ["plastic"] },
  { name: "Prince", cats: ["plastic"] },
  { name: "Supreme", cats: ["plastic"] },

  // Steel and brass: almost everything is unbranded local work. These few do
  // turn up on boxed dinner sets and vacuum flasks rather than on loose goods.
  { name: "Jayna", cats: ["steel"] },
  { name: "Neelam", cats: ["steel"] },
];

/** Trimmed, collapsed whitespace, for comparing two spellings of one brand. */
const normalise = (s) => (s || "").trim().replace(/\s+/g, " ");
const key = (s) => normalise(s).toLowerCase();

/**
 * Brands to offer, best first.
 *
 * The shop's own come before the seeded ones -- what a shop actually stocks
 * beats anything shipped with the app, the same principle as sizeChipsFor in
 * families.js.
 *
 * Both halves are narrowed by category. Leaving the shop's own unfiltered
 * looked helpful and was not: a steel thali was offered Prestige and Baltra
 * because the shop sells cookers and kettles, which is exactly the noise this
 * list was meant to remove.
 *
 * Typing overrides that. A brand used anywhere in the shop is offered once its
 * name is being typed, so a name that genuinely spans categories is one or two
 * letters away rather than hidden.
 */
export function suggestBrands(query, category, products = [], limit = 8) {
  const q = key(query);
  const seen = new Set();
  const mine = [];
  const seeded = [];

  const take = (name, into) => {
    const k = key(name);
    if (!k || seen.has(k)) return;
    seen.add(k);
    into.push(normalise(name));
  };

  for (const p of products) {
    if (!p.brand) continue;
    if (q) {
      // Typed: match across the whole shop, category be damned.
      if (!key(p.brand).includes(q)) continue;
    } else if (category && p.category !== category) {
      // Nothing typed: only what this shop puts in this category.
      continue;
    }
    take(p.brand, mine);
  }

  for (const b of BRANDS) {
    if (category && !b.cats.includes(category)) continue;
    if (q && !key(b.name).includes(q)) continue;
    take(b.name, seeded);
  }

  // Within each half, a name starting with what was typed comes first.
  const rank = (list) => {
    if (!q) return list;
    return [
      ...list.filter((n) => key(n).startsWith(q)),
      ...list.filter((n) => !key(n).startsWith(q)),
    ];
  };

  return [...rank(mine), ...rank(seeded)].slice(0, limit);
}
